import { apiClient } from './apiClient';
import { formatErrorMessage } from '../utils/authFormUtils';

export type TtqMembershipPressureRejectReasonCode =
  | 'not_homogeneous'
  | 'trunk_mismatch'
  | 'ability_mismatch'
  | 'duplicate_or_redundant'
  | 'wrong_ttqa_target'
  | 'not_worth_tasting_group'
  | 'other';

export type TtqMembershipPressureRecommendation = {
  id: string;
  userId: string;
  subjectCode: 'math' | 'yuwen';
  sourceParticleId: string;
  sourceParticleTitle?: string | null;
  /** Stem / main question for human review (from AF enrichment). */
  sourceMainQuestion?: string | null;
  action: 'join_existing_ttqa' | 'open_new_ttqa';
  targetTtqaId: string | null;
  /** Existing TTQA title when action is join_existing_ttqa. */
  targetTtqaTitle?: string | null;
  targetTtqaMemberCount?: number | null;
  targetTtqaMemberPreviews?: Array<{
    particleId: string;
    title: string | null;
    mainQuestion: string | null;
  }>;
  suggestedPeerIds: string[];
  suggestedPeerTitles?: Array<string | null>;
  suggestedPeerMainQuestions?: Array<string | null>;
  /** Existing TTQA membership for each suggested peer (aligned with suggestedPeerIds). */
  suggestedPeerMemberships?: Array<{
    inTtqa: boolean;
    sessions: Array<{ id: string; title: string | null }>;
  }>;
  suggestedPeerSpineLabels?: Array<string | null>;
  sourceSpineLabel?: string | null;
  sourceParticleMembership?: {
    inTtqa: boolean;
    sessions: Array<{ id: string; title: string | null }>;
  } | null;
  radarThresholds?: {
    trunkProximity: number;
    typeHomogeneity: number;
    implicitAbility: number;
    total: number;
  };
  scoreWeights?: {
    trunkProximity: number;
    typeHomogeneity: number;
    implicitAbility: number;
    mathStructure: number;
  };
  sourceExamPointLabels?: string[];
  sourceAbilityKeys?: string[];
  scoreSeries?: Array<{
    role: 'peer';
    particleId: string;
    label: string;
    ring: 0 | 1 | 2 | null;
    scores: {
      trunkProximity: number;
      typeHomogeneity: number;
      implicitAbility: number;
      mathStructure: number;
      total: number;
    };
    examPointLabels?: string[];
    abilityKeys?: string[];
    sharedExamPointLabels?: string[];
    sharedAbilityKeys?: string[];
  }>;
  scores: {
    trunkProximity: number;
    typeHomogeneity: number;
    implicitAbility: number;
    mathStructure: number;
    total: number;
  };
  ringEvidence: {
    ring: 0 | 1 | 2;
    sharedAbilityKeys?: string[];
    notes?: string[];
  };
  status: 'recommended' | 'approved' | 'rejected' | 'superseded';
  createdAt: string;
};

export type TtqMembershipPressureWorkerStatus = {
  userId: string;
  lastReconcileAt: string | null;
  lastReconcileEnqueued: number;
  cooldownRemainingMs: number;
  reconcileAllowed: boolean;
  valveEnabled: boolean;
};

export type TtqMembershipPressureParticlePreview = {
  particleId: string;
  title: string | null;
  mainQuestion: string | null;
};

export type TtqMembershipPressureDebugScanItem = {
  particleId: string;
  title: string | null;
  mainQuestion?: string | null;
  scan: {
    recommendationCount: number;
    scoredCandidateCount: number;
    skippedReason?: string;
    dryRun?: boolean;
    topCandidates?: Array<{
      particleId: string;
      ring: 0 | 1 | 2;
      scores: {
        trunkProximity: number;
        typeHomogeneity: number;
        implicitAbility: number;
        mathStructure: number;
        total: number;
      };
    }>;
    topCandidateTitles?: Array<string | null>;
    proposals?: Array<{
      action: 'join_existing_ttqa' | 'open_new_ttqa';
      targetTtqaId: string | null;
      suggestedPeerIds: string[];
      scores: {
        trunkProximity: number;
        typeHomogeneity: number;
        implicitAbility: number;
        mathStructure: number;
        total: number;
      };
      ringEvidence?: {
        ring: 0 | 1 | 2;
        notes?: string[];
        sharedAbilityKeys?: string[];
      };
    }>;
    proposalPeerTitles?: Array<Array<string | null>>;
  };
};

export type TtqMembershipPressureDebugScanResult = {
  userId: string;
  subjectCode: string;
  valveEnabled: boolean;
  skippedReason?: string;
  commit: boolean;
  orphanTotal: number;
  scannedCount: number;
  proposalCount?: number;
  recommendationWritten?: number;
  particlePreviews?: Record<string, TtqMembershipPressureParticlePreview>;
  items: TtqMembershipPressureDebugScanItem[];
};

export const TTQ_MEMBERSHIP_PRESSURE_REJECT_REASONS: Array<{
  code: TtqMembershipPressureRejectReasonCode;
  label: string;
}> = [
  { code: 'not_homogeneous', label: '同质化不足' },
  { code: 'trunk_mismatch', label: '主干不宜同品' },
  { code: 'ability_mismatch', label: '隐性能力不合' },
  { code: 'duplicate_or_redundant', label: '与已有成员冗余' },
  { code: 'wrong_ttqa_target', label: '应进别的品题' },
  { code: 'not_worth_tasting_group', label: '不值得聚合' },
  { code: 'other', label: '其他' },
];

export function formatMembershipPressureWorkerScanLabel(
  status: TtqMembershipPressureWorkerStatus | null | undefined,
): string {
  if (!status) return '背压扫描状态加载中…';
  if (!status.lastReconcileAt) {
    return '尚无 AppWorker 背压扫描记录（请确认常驻 Worker 已启动）';
  }
  const at = new Date(status.lastReconcileAt);
  const absolute = Number.isNaN(at.getTime())
    ? status.lastReconcileAt
    : at.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  const ageMs = Date.now() - at.getTime();
  const ageMin = Math.max(0, Math.floor(ageMs / 60_000));
  const relative = ageMin < 1 ? '刚刚' : ageMin < 60 ? `${ageMin} 分钟前` : `${Math.floor(ageMin / 60)} 小时前`;
  const enqueued = status.lastReconcileEnqueued > 0
    ? `，当次入队 ${status.lastReconcileEnqueued}`
    : '';
  return `上次背压扫描 ${absolute}（${relative}）${enqueued}`;
}

/** Client only reads/acts on recommendations. Heartbeat reconcile is AppWorker-only. */
export const ttqMembershipPressureService = {
  async getWorkerStatus() {
    try {
      return await apiClient.getEnvelope<TtqMembershipPressureWorkerStatus>(
        '/api/ttq/membership-pressure/worker-status',
      );
    } catch (error) {
      throw new Error(formatErrorMessage(error, '加载背压扫描状态失败'));
    }
  },

  /** Manual AF-side debug scan. Default dry-run (does not write recommendations). */
  async debugScan(input?: { commit?: boolean; limit?: number; subjectCode?: string }) {
    try {
      return await apiClient.postEnvelope<TtqMembershipPressureDebugScanResult>(
        '/api/ttq/membership-pressure/debug-scan',
        {
          commit: Boolean(input?.commit),
          limit: input?.limit,
          subjectCode: input?.subjectCode,
        },
      );
    } catch (error) {
      throw new Error(formatErrorMessage(error, '手动扫描失败'));
    }
  },

  async listRecommendations(status: 'recommended' | 'all' = 'recommended') {
    try {
      const rows = await apiClient.getEnvelope<TtqMembershipPressureRecommendation[]>(
        `/api/ttq/membership-pressure/recommendations?status=${encodeURIComponent(status)}`,
      );
      return Array.isArray(rows) ? rows : [];
    } catch (error) {
      throw new Error(formatErrorMessage(error, '加载入围推荐失败'));
    }
  },

  async approve(recommendationId: string, input?: { peerParticleIds?: string[]; title?: string }) {
    try {
      return await apiClient.postEnvelope<{ session?: { id: string }; recommendationId: string }>(
        `/api/ttq/membership-pressure/recommendations/${encodeURIComponent(recommendationId)}/approve`,
        {
          peerParticleIds: input?.peerParticleIds,
          title: input?.title,
        },
      );
    } catch (error) {
      throw new Error(formatErrorMessage(error, '扶正失败'));
    }
  },

  async reject(
    recommendationId: string,
    input: { reasonCode: TtqMembershipPressureRejectReasonCode; reasonText?: string },
  ) {
    try {
      return await apiClient.postEnvelope<unknown>(
        `/api/ttq/membership-pressure/recommendations/${encodeURIComponent(recommendationId)}/reject`,
        input,
      );
    } catch (error) {
      throw new Error(formatErrorMessage(error, '否定失败'));
    }
  },
};
